<template>
	<div class="planner-datebar">
		<div class="container">
			<div class="row">
				<div class="col-6 col-lg-2 order-1 d-flex align-items-center pt-2 pt-lg-0">
					<div class="display-month" aria-label="Month Displayed" data-testid="display-month">
						{{ displayMonth }}
					</div>
				</div>

				<div class="col-lg-8 order-3 order-lg-2 d-flex">
					<button
						v-adobe-data-layer="calADLConfig"
						class="btn btn-simple d-none d-lg-inline"
						:disabled="prevDisabled"
						data-testid="prev-week"
						@click="prevWeek"
					>
						<i class="bi-chevron-left"></i>
						<span class="visually-hidden">Previous Week</span>
					</button>

					<div ref="datepicker" class="datepicker" data-testid="datepicker" @scroll="handleScroll">
						<div
							v-for="(week, weekIndex) in weeks"
							:key="weekIndex"
							:data-week-index="weekIndex"
							class="week"
						>
							<button
								v-for="(day, dayIndex) in week"
								:key="dayIndex"
								v-adobe-data-layer="dayADLConfig"
								class="btn btn-simple btn-day"
								:aria-current="isToday(day.dayObject) ? 'date' : undefined"
								data-testid="day-click"
								:class="{
									selected: isDaySelected(day.dayObject),
									today: isToday(day.dayObject),
									empty: !hasClasses(day)
								}"
								@click="onDayClick(day.dayObject)"
							>
								<div aria-hidden="true">
									{{ formatDate(day.dayObject, 'dd').charAt(0) }}<br />
									<span class="day-number"> {{ formatDate(day.dayObject, 'D') }} </span>
								</div>
								<div class="visually-hidden">
									<span>{{ formatDate(day.dayObject, 'dddd MMMM D') }}</span>
								</div>
							</button>
						</div>
					</div>

					<button
						v-adobe-data-layer="calADLConfig"
						class="btn btn-simple d-none d-lg-inline"
						:disabled="nextDisabled"
						data-testid="next-week"
						@click="nextWeek"
					>
						<i class="bi-chevron-right"></i>
						<span class="visually-hidden">Next Week</span>
					</button>
				</div>

				<div
					class="col-6 col-lg-2 order-2 order-lg-3 d-flex align-items-center justify-content-end pt-2 pt-lg-0"
				>
					<button
						class="btn btn-simple btn-icon text-uppercase"
						data-testid="toggle-filters"
						@click="toggleFilters"
					>
						<FiltersIcon></FiltersIcon>
						Filters
						<span v-if="selectedFilters.length" class="filter-count">
							{{ selectedFilters.length }}
						</span>
					</button>
					<ClassFilters v-model="showFilters"></ClassFilters>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useScheduleStore } from '@/stores/schedule';
import { useDates } from '@/composables/use-dates';
import { useDebounceFn } from '@vueuse/core';
import vAdobeDataLayer from '@/directives/AdobeDataLayer';
import { chunk } from '@/utils/array';
import ClassFilters from '@/components/class-schedule/ClassFilters.vue';
import FiltersIcon from '@/components/icons/FiltersIcon.vue';
import type { Dayjs } from 'dayjs';
import type { AempschedDay, IADLConfig } from 'Types/aempsched/aempschedDay';

const datepicker: Ref<HTMLElement | null> = ref(null);
const handleScroll = useDebounceFn((event: Event) => {
	const target = event.target as HTMLElement;
	const prevSelectedWeek = selectedWeek.value;
	selectedWeek.value = Math.round(target.scrollLeft / target.clientWidth);
	if (prevSelectedWeek > selectedWeek.value) {
		getData(sunday.value, saturday.value);
		setSelectedDay(selectedDay.value.subtract(7, 'days'));
	}
	if (prevSelectedWeek < selectedWeek.value) {
		getData(sunday.value, saturday.value);
		setSelectedDay(selectedDay.value.add(7, 'days'));
	}
}, 250);

const scheduleStore = useScheduleStore();
const { days, today, selectedDay, selectedFilters } = storeToRefs(scheduleStore);
const { setSelectedDay, daysInit, getData } = scheduleStore;

const { formatDate } = useDates();
const weeks = computed(() => chunk<AempschedDay>(days.value, 7));

const selectedWeek = ref(0);

onBeforeMount(() => {
	daysInit();
});

const calADLConfig: IADLConfig = {
	name: 'prospectschedules',
	type: 'button',
	title: 'calendar week interaction'
};

const dayADLConfig: IADLConfig = {
	name: 'prospectschedules',
	type: 'button',
	title: 'calendar day interaction'
};

const showFilters = ref(false);
const prevDisabled = computed(() => selectedWeek.value === 0);
const nextDisabled = computed(() => selectedWeek.value === 7);
const sunday = computed(() => weeks.value[selectedWeek.value][0].dayObject);
const saturday = computed(() => weeks.value[selectedWeek.value][6].dayObject);

const displayMonth = computed(() => {
	if (days.value.length === 0) {
		return 'JAN';
	}

	if (sunday.value.month() !== saturday.value.month()) {
		return `${formatDate(sunday.value, 'MMM')} / ${formatDate(saturday.value, 'MMM')}`;
	}

	return formatDate(sunday.value, 'MMM');
});

const prevWeek = () => {
	selectedWeek.value--;
	if (datepicker.value) {
		datepicker.value.scrollTo({
			left: datepicker.value.scrollLeft - datepicker.value.clientWidth,
			behavior: 'smooth'
		});
	}
	getData(sunday.value, saturday.value);
	setSelectedDay(selectedDay.value.subtract(7, 'days'));
};

const nextWeek = () => {
	selectedWeek.value++;
	if (datepicker.value) {
		datepicker.value.scrollTo({
			left: datepicker.value.scrollLeft + datepicker.value.clientWidth,
			behavior: 'smooth'
		});
	}
	getData(sunday.value, saturday.value);
	setSelectedDay(selectedDay.value.add(7, 'days'));
};

const onDayClick = (day: Dayjs) => {
	setSelectedDay(day);
};

const isDaySelected = (day: Dayjs) => {
	return selectedDay.value.isSame(day, 'day');
};

const isToday = (day: Dayjs) => {
	return today.value.isSame(day, 'day');
};

const hasClasses = (day: AempschedDay) => {
	return day.classes.length > 0;
};

const toggleFilters = () => {
	showFilters.value = !showFilters.value;
};

defineExpose({
	handleScroll,
	selectedDay,
	selectedWeek
});
</script>

<style lang="scss" scoped>
$class-sched-border-color: rgba($border-color, 0.15);
.planner-datebar {
	position: sticky;
	top: 0;
	background-color: var(--kpl-body-bg);
	z-index: 9;

	&::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 1px;
		background-color: $class-sched-border-color;
		z-index: -1;
	}
}

.datepicker {
	overflow-x: auto;
	scroll-snap-type: x mandatory;
	white-space: nowrap;

	&::-webkit-scrollbar {
		display: none;
	}
}

.filter-count {
	width: 24px;
	height: 24px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: solid 1px var(--kpl-border-color-translucent);
	border-radius: 50%;
	padding-left: 1px;
}

.week {
	display: inline-flex;
	width: 100%;
	scroll-snap-align: start;
	scroll-snap-stop: always;
}

.btn-day {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	flex-direction: column;
	flex: 0 0 auto;
	width: 14.285714285%;
	min-height: 5.5rem;
	position: relative;
	padding-top: 1.25rem;
	line-height: 1;
	border-radius: 0;
}

.day-number {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 32px;
	width: 32px;
	margin: 0.5rem 0;
	padding-left: 1px;
	border-radius: 50%;
	font-weight: 700;

	.today & {
		border: 1px solid color-mix(in sRGB, currentColor 25%, transparent);
	}

	.empty & {
		font-weight: 400;
		opacity: 0.5;
	}

	.selected & {
		background-color: var(--kpl-body-color);
		color: var(--kpl-body-bg);
		opacity: 1;
	}
}

.display-month {
	opacity: 0.75;
	text-transform: uppercase;
}
</style>
